import { computed, ComputedRef, reactive } from "vue";
import axios from "axios";

import { logError } from "@Helpers/logger.js";
import { ParseServerError } from "@Modules/shared/Api/ServerErrors";
import ReacitvePaginator, { ReactivePaginator as iReactivePaginator } from "@Resources/js/api/ReactivePaginator.ts";
import { TypedAliasData, TypedCursorPage } from "@Resources/js/laravel";
import { Alias } from "@Modules/urls/Data/Models/AliasFactory.ts";
import UrlAliasFactory, { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory.ts";
import { buildFilterQuery } from "@Modules/urls/Helpers/buildFilterQuery";
import VisitOrganizationOrTeam from "@Modules/teams/Api/Visitors/VisitOrganizationOrTeam.ts";
import VisitTag from "@Modules/teams/Api/Visitors/VisitTag.ts";
import TagModel, { Tag } from "../Data/Models/TagModel.ts";

/**
 * The filter argument of each fetch method allows you to filter through data for a specific urls constrained by
 * the method itself. For example. you might want to return only urls that belong to a team that are currently
 * active. The default behaviour is to just return all urls
 */
const DefaultFilter = function () {
    return {
        type: "available" as const,
    }
}

/**
 * The search argument of each fetch method allows you to search through data for a specific record constrained by
 * the method itself. For example. you might want to search to see if a team has a url with a specific alias or
 * tag. By default, we don't do any searching.
 */
const DefaultSearch = function () {
    return {
        searchType: "alias" as const,
        searchValue: "",
    }
}

export type FetchUrlOptions = {
    orgID: string;
} & Partial<{
    teamID: string | null;
    filter: {
        type: "available" | "archived" | "all"
    }
    search: {
        searchType: "none" | "alias" | "tag",
        searchValue: string | null
    };
    clear: boolean;
    includeAlias: string | null;
}>;

/**
 * Available custom errors.
 */
export enum CustomUrlsErrors {
  FETCH_FAIL = "fetch-fail",
  DEFAULT = "default",
};

export type LastFetchState = Omit<FetchUrlOptions, "clear" | "orgID"> & {
    orgID: string | null;
};

export type CreateUrlOptions = {
    orgSlug: string;
    teamSlug: string;
    url: string;
    domain: string;
    alias?: string;
    description?: string;
    tags?: string[];
    expires_at?: string | null;
};

/**
 * Options for updating an existing URL. The original domain and alias identify the
 * record being changed, as the update may itself change the domain or alias.
 */
export type UpdateUrlOptions = CreateUrlOptions & Partial<{
    originalDomain: string;
    originalAlias: string;
}>;

export type UrlIdentifierOptions = {
    orgSlug: string;
    teamSlug: string | null;
    domain: string;
    alias: string;
};

export type SetStatsTrackingOptions = UrlIdentifierOptions & {
    enabled: boolean;
};

export type UrlsErrors = Record<CustomUrlsErrors | string, string>;

export type UrlsApi = {
    paginator: ComputedRef<iReactivePaginator<UrlAlias>>,
    urls: ComputedRef<UrlAlias[]>,
    loading: ComputedRef<boolean>,
    sending: ComputedRef<boolean>,
    downloadUrl: ComputedRef<string | null>,
    errors: ComputedRef<UrlsErrors>,
    lastFetch: ComputedRef<LastFetchState>,
    clearUrls: () => void,
    fetchTeamUrls: (options: FetchUrlOptions) => Promise<void>,
    createUrl: (options: CreateUrlOptions) => Promise<UrlAlias | null>,
    updateUrl: (options: UpdateUrlOptions) => Promise<boolean>,
    deleteUrl: (options: UrlIdentifierOptions) => Promise<boolean>,
    archiveUrl: (options: UrlIdentifierOptions) => Promise<boolean>,
    setStatsTracking: (options: SetStatsTrackingOptions) => Promise<boolean>,
    setRouter: (router: Pick<HttpInterface, "get" | "post" | "delete" | "patch">) => UrlsApi,
    clearErrors: () => void,
}

export default function UrlsApi(): UrlsApi {
    const paginator = ReacitvePaginator<UrlAlias>({});
    const state = reactive<{
        sending: boolean;
        loading: boolean;
        lastFetch: LastFetchState;
        errors: UrlsErrors;
    }>({
        sending: false,
        loading: false,
        lastFetch: {
            orgID: null,
            teamID: null,
            filter: { type: "available" },
            search: { searchType: "alias", searchValue: "" },
        },
        errors: {},
    });

    let router: Pick<HttpInterface, "get" | "post" | "delete" | "patch"> = axios;

    function makeVisitAlias(orgID: string, teamID: string | null): (alias: Alias, query?: Record<string, string>) => void {
        return (alias: Alias, query?: Record<string, string>): void => {
            VisitOrganizationOrTeam({
                orgSlug: orgID,
                teamSlug: teamID,
                alias: `${alias.domain}/${alias.alias}`,
                query: { ...buildFilterQuery(state.lastFetch), ...query },
            });
        };
    }

    function makeVisitTag(orgID: string, teamID: string | null): (tag: Tag, query?: Record<string, string>) => void {
        return (tag: Tag, query?: Record<string, string>): void => {
            VisitTag({ orgSlug: orgID, teamSlug: teamID, tagName: tag.name, query: { ...buildFilterQuery(state.lastFetch), ...query } });
        };
    }

    /**
     * Updates fields on an alias in the paginator.
     *
     * Vue reactivity requires creating new objects rather than mutating existing ones.
     * We convert Alias objects (which have Tag[] for tags) back to raw format (string[] for tags)
     * so that UrlAliasFactory can properly recreate them through AliasFactory.
     */
    function updateAliasInPaginator(domain: string, alias: string, updates: Partial<Alias>, newUrl?: string): void {
        if (!paginator.page?.data) {
            return;
        }

        const index = paginator.page.data.findIndex((urlAlias: UrlAlias) => {
            const main = urlAlias.mainAlias();
            return main?.domain === domain && main?.alias === alias;
        });

        const urlAlias = paginator.page.data[index];
        if (index === -1 || !urlAlias) {
            return;
        }

        // Convert Alias objects to raw format (Tag[] -> string[]) for the factory
        const updatedAliases = urlAlias.aliases.map((a) => {
            const rawAlias = { ...a, tags: a.tags.map((t: Tag) => t.name) };
            if (a.domain === domain && a.alias === alias) {
                const rawUpdates = updates.tags
                    ? { ...updates, tags: updates.tags.map((t: Tag) => t.name) }
                    : updates;
                return { ...rawAlias, ...rawUpdates };
            }
            return rawAlias;
        });

        // Create new UrlAlias through factory to maintain Vue reactivity
        const { orgID, teamID } = state.lastFetch;
        const updatedUrlAlias = UrlAliasFactory({
            ...urlAlias,
            url: newUrl !== undefined ? newUrl : urlAlias.url,
            aliases: updatedAliases,
        } as TypedAliasData)
            .setVisit(makeVisitAlias(orgID ?? "", teamID ?? ""))
            .setTagVisit(makeVisitTag(orgID ?? "", teamID ?? ""));

        // Replace in array with new objects to trigger Vue reactivity
        paginator.page.data = [
            ...paginator.page.data.slice(0, index),
            updatedUrlAlias,
            ...paginator.page.data.slice(index + 1),
        ];
    }

    function clearErrors(): void {
      state.errors = {};
    }

    /**
    * A method used to set the reactive errors variable. We use an external method so that we can also pass
    * it to the paginator
    */
    const setErrors = function(
      theUrl: string,
      e: unknown,
      customError: UrlsErrors = {},
    ): void {
      const { status, fieldErrors } = ParseServerError(e);
      logError(`server status:${status} url: ${theUrl}`);
      const errors: UrlsErrors = {
        ...state.errors,
        ...customError,
      };

      Object.entries(fieldErrors ?? {}).forEach(([key, val]) => {
        errors[key] = val[0] ?? "";
      });

      state.errors = errors;
    };

    return {
        urls: computed(() => paginator?.page?.data || []),
        paginator: computed(() => paginator),
        sending: computed(() => state.sending),
        loading: computed(() => state.loading || paginator.loading.value),
        errors: computed(() => state.errors),
        lastFetch: computed(() => state.lastFetch),

        downloadUrl: computed(() => {
            const { orgID, teamID, filter, search } = state.lastFetch;

            if (!orgID) {
                return null;
            }

            let baseUrl: string;

            if (!teamID) {
                baseUrl = `/app/organizations/${orgID}/urls/${filter?.type}/csv`;
            } else if (teamID === "personal") {
                baseUrl = `/app/organizations/${orgID}/personal/urls/${filter?.type}/csv`;
            } else {
                baseUrl = `/app/organizations/${orgID}/team/${teamID}/urls/${filter?.type}/csv`;
            }

            if (search?.searchType !== "none" && search?.searchValue && search.searchValue.trim().length > 0) {
                const searchParam = `${search.searchType}:${search.searchValue.trim()}`;
                baseUrl += `?search=${encodeURIComponent(searchParam)}`;
            }

            return baseUrl;
        }),

        /**
         * Clears out all currently loaded urls
         */
        clearUrls: () => paginator.clearAllData(),

        clearErrors: clearErrors,

        /**
         * Fetches a list of urls for an organization or workspace
         * @param orgID the organization ID
         *
         * @param teamID The id of the workspace you want urls for.
         *                    If null we fetch urls for all workspaces in the org
         *                    If 'personal' we fetch urls from the users personal team
         *
         * @param filter allows you to filter the returned results to spedific criteria
         *
         * @param search allows you to search for specific urls
         *
         * @param Boolean clear If true we clear all urls before performing the search
         *                      If false we append the urls to the end of the list of urls
         */
        fetchTeamUrls: async function ({
            orgID,
            teamID = null,
            filter = DefaultFilter(),
            search = DefaultSearch(),
            clear = false,
            includeAlias,
        }: FetchUrlOptions) {
            if (clear) {
                paginator.page.data = [];
            }

            const visitAlias = makeVisitAlias(orgID, teamID);
            const visitTag = makeVisitTag(orgID, teamID);

            clearErrors();
            state.loading = true;

            state.lastFetch = {
                orgID,
                teamID,
                filter,
                search,
            };

            let theUrl = `/app/api/organization/${orgID}${teamID ? "/teams/" + teamID : ""}/urls/${filter.type}`;

            const params: string[] = [];
            if (search.searchType !== "none" && search.searchValue && search.searchValue.trim().length > 0) {
                const searchParam = `${search.searchType}:${search.searchValue.trim()}`;
                params.push(`search=${encodeURIComponent(searchParam)}`);
            }
            if (includeAlias) {
                params.push(`include_alias=${encodeURIComponent(includeAlias)}`);
            }
            if (params.length > 0) {
                theUrl += `?${params.join("&")}`;
            }

            try {
                const response = await router.get<TypedCursorPage<TypedAliasData>>(theUrl);
                paginator.setFormatter((item: TypedAliasData) =>
                    UrlAliasFactory(item)
                        .setVisit(visitAlias)
                        .setTagVisit(visitTag),
                )
                    .setErrorHandler((url, event) => setErrors(url, event, { [CustomUrlsErrors.FETCH_FAIL]: "Fetch fail!"}))
                    .replacePage(response.data);
            } catch (e) {
                setErrors(theUrl, e, { [CustomUrlsErrors.FETCH_FAIL]: "Fetch fail!"});
            } finally {
                state.loading = false;
            }
        },

        /**
         * Creates a new shortened URL
         */
        createUrl: async function ({
            orgSlug,
            teamSlug,
            url,
            domain,
            alias,
            description,
            tags,
            expires_at,
        }: CreateUrlOptions): Promise<UrlAlias | null> {
            state.sending = true;
            state.errors = {};

            const requestUrl = `/app/api/organization/${orgSlug}/team/${teamSlug}/url/create`;

            try {
                const response = await router.post<{ data: TypedAliasData[] }>(requestUrl, {
                    url,
                    domain,
                    alias: alias || "",
                    description: description || "",
                    tags: tags || [],
                    expires_at,
                }, { DISABLE_ERROR_INTERCEPTORS: true });

                const urlData = response.data.data;
                const firstUrl = urlData[0];

                if (!firstUrl) {
                    return null;
                }

                const visitAlias = makeVisitAlias(orgSlug, teamSlug);
                const visitTag = makeVisitTag(orgSlug, teamSlug);

                const urlAlias = UrlAliasFactory(firstUrl)
                    .setVisit(visitAlias)
                    .setTagVisit(visitTag);

                paginator.prepend(urlAlias);

                return urlAlias;
            } catch (error) {
                setErrors(requestUrl, error);

                return null;
            } finally {
                state.sending = false;
            }
        },

        /**
         * Updates an existing URL. First updates metadata via /update, then changes the destination URL via /change.
         */
        updateUrl: async function ({
            orgSlug,
            teamSlug,
            url,
            domain,
            alias,
            description,
            tags,
            expires_at,
            originalDomain,
            originalAlias,
        }: UpdateUrlOptions): Promise<boolean> {
            state.sending = true;
            state.errors = {};

            const teamPath = teamSlug ? `/team/${teamSlug}` : "";

            try {
                const updateEndpoint = `/app/api/organization/${orgSlug}${teamPath}/url/update`;
                await router.post(updateEndpoint, {
                    domain: originalDomain,
                    alias: originalAlias,
                    new_alias: alias || "",
                    new_domain: domain,
                    new_description: description || "",
                    new_tags: tags || [],
                    new_expires_at: expires_at,
                }, { DISABLE_ERROR_INTERCEPTORS: true });

                const changeEndpoint = `/app/api/organization/${orgSlug}${teamPath}/url/change`;
                await router.post(changeEndpoint, {
                    domain: domain || originalDomain,
                    alias: alias || originalAlias,
                    url,
                }, { DISABLE_ERROR_INTERCEPTORS: true });

                updateAliasInPaginator(originalDomain ?? "", originalAlias ?? "", {
                    domain,
                    alias: alias || originalAlias,
                    description: description || "",
                    tags: (tags || []).map((tagName: string) => TagModel(tagName)),
                    expires_at,
                    tiny_url: `https://${domain}/${alias || originalAlias}`,
                }, url);

                return true;
            } catch (error) {
                const endpoint = `/app/api/organization/${orgSlug}${teamPath}/url`;
                setErrors(endpoint, error);
                return false;
            } finally {
                state.sending = false;
            }
        },

        /**
         * Deletes a URL from the server
         */
        deleteUrl: async function ({
            orgSlug,
            teamSlug,
            domain,
            alias,
        }: UrlIdentifierOptions): Promise<boolean> {
            state.sending = true;
            state.errors = {};

            const teamPath = teamSlug ? `/team/${teamSlug}` : "";
            const requestUrl = `/app/api/organization/${orgSlug}${teamPath}/url/alias/${domain}/${alias}`;

            try {
                await router.delete(requestUrl);

                this.clearUrls();
                await this.fetchTeamUrls({
                  orgID: orgSlug,
                  teamID: teamSlug,
                  filter: state.lastFetch.filter,
                  search: state.lastFetch.search,
                });

                return true;
            } catch (error) {
                logError(`Failed to delete URL: ${domain}/${alias}`);
                return false;
            } finally {
                state.sending = false;
            }
        },

        /**
         * Toggles the archived state of a URL
         */
        archiveUrl: async function ({
            orgSlug,
            teamSlug,
            domain,
            alias,
        }: UrlIdentifierOptions): Promise<boolean> {
            state.sending = true;
            state.errors = {};

            const teamPath = teamSlug ? `/team/${teamSlug}` : "";
            const requestUrl = `/app/api/organization/${orgSlug}${teamPath}/url/archive`;

            try {
                await router.patch(requestUrl, { domain, alias });

                this.clearUrls();
                await this.fetchTeamUrls({
                  orgID: orgSlug,
                  teamID: teamSlug,
                  filter: state.lastFetch.filter,
                  search: state.lastFetch.search,
                });

                return true;
            } catch (error) {
                logError(`Failed to archive URL: ${domain}/${alias}`);
                return false;
            } finally {
                state.sending = false;
            }
        },

        /**
         * Enables or disables analytics collection for a URL
         */
        setStatsTracking: async function ({
            orgSlug,
            teamSlug,
            domain,
            alias,
            enabled,
        }: SetStatsTrackingOptions): Promise<boolean> {
            state.sending = true;
            state.errors = {};

            const teamPath = teamSlug ? `/team/${teamSlug}` : "";
            const requestUrl = `/app/api/organization/${orgSlug}${teamPath}/url/analytics/status`;

            try {
                const response = await router.patch<{ data: TypedAliasData }>(
                    requestUrl,
                    { domain, alias, enabled },
                    { DISABLE_ERROR_INTERCEPTORS: true },
                );

                const updatedAlias = (response.data?.data?.aliases ?? []).find(
                    (a) => a.domain === domain && a.alias === alias,
                );

                updateAliasInPaginator(domain, alias, {
                    stats: updatedAlias?.stats ?? { enabled, public: false },
                } as Partial<Alias>);

                return true;
            } catch (error) {
                setErrors(requestUrl, error);
                return false;
            } finally {
                state.sending = false;
            }
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter) {
            router = newRouter;
            return this;
        },
    };
}
